import { useQuery } from '@apollo/client';
import Hubspot from '@clearkit/icons/glyphs/Hubspot';
import Marketo from '@clearkit/icons/glyphs/Marketo';
import Salesforce from '@clearkit/icons/glyphs/Salesforce';
import Segment from '@clearkit/icons/glyphs/SegmentCompany';
import { Subscribe } from 'unstated';

import IntegrationPickerCard from '~/components/IntegrationPickerCard';
import {
  IntegrationPickerLayout,
  IntegrationPickerRow,
} from '~/components/layouts/IntegrationPickerLayout';
import SidebarDrawer from '~/components/SidebarDrawer';
import UpgradeAccountForm from '~/components/UpgradeAccountForm';
import UserContainer from '~/containers/User';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import Tile from '~/images/Tiles/Enrichment.svg';

import query from './query.graphql';

const LegacyEnrichmentList = () => {
  const { data: { account } = {}, loading } = useQuery(query);
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);

  const hasHubspotFeatureFlag = useFeatureFlag('x_data_enrichment_hubspot');
  const hasMarketoFeatureFlag = useFeatureFlag('x_marketo_on_platform_beta');

  const hubspotUrl = hasHubspotFeatureFlag.enabled
    ? '/enrichment/hubspot'
    : 'hubspot.clearbit.com';
  const hubspotVariant = hasHubspotFeatureFlag.enabled
    ? IntegrationPickerCard.Variants.default
    : IntegrationPickerCard.Variants.external;

  const marketoUrl = hasMarketoFeatureFlag.enabled
    ? '/enrichment/marketo'
    : 'marketo.clearbit.com';

  const marketoVariant = hasMarketoFeatureFlag.enabled
    ? IntegrationPickerCard.Variants.default
    : IntegrationPickerCard.Variants.external;

  const integrations = [
    {
      Icon: Salesforce,
      type: 'salesforce',
      bgClass: 'bg-salesforce',
      title: 'Salesforce',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Salesforce.',
      tag: 'Accelerate Revenue',
      url: '/enrichment/salesforce',
      plan: 'enrichment_salesforce',
    },
    {
      Icon: Marketo,
      type: 'marketo',
      bgClass: 'bg-marketo',
      title: 'Marketo',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Marketo.',
      tag: 'Accelerate Revenue',
      url: marketoUrl,
      plan: 'enrichment_marketo',
      variant: marketoVariant,
    },
    {
      Icon: Segment,
      type: 'segment',
      bgClass: 'bg-segment',
      title: 'Segment',
      text: 'Enrich people and companies in Segment with Clearbit data.',
      tag: 'Convert Intent',
      url: 'segment.clearbit.com',
      plan: 'enrichment',
      variant: IntegrationPickerCard.Variants.external,
    },
    {
      Icon: Hubspot,
      type: 'hubspot',
      bgClass: 'bg-hubspot',
      title: 'Hubspot',
      text:
        'Enrich and refresh new contacts, form submissions, or your entire contact database within HubSpot.',
      tag: 'Accelerate Revenue',
      url: hubspotUrl,
      plan: 'enrichment_hubspot',
      variant: hubspotVariant,
    },
  ];

  const accesibleIntegrations = [];
  const unaccesibleIntegrations = [];

  integrations.forEach((integration) => {
    if (account?.plans.some((plan) => plan === integration.plan)) {
      accesibleIntegrations.push(integration);
    } else {
      unaccesibleIntegrations.push(integration);
    }
  });

  return (
    <Subscribe to={[UserContainer]}>
      {(userContainer) => {
        return (
          <IntegrationPickerLayout
            background="white"
            title="Data Sync"
            subtitle="Get rich firmographic data into the systems that matter most to your business"
            loading={loading}
            tile={<Tile />}>
            <div className="flex flex-col flex-grow">
              {accesibleIntegrations.length > 0 && (
                <IntegrationPickerRow
                  title="Your integrations"
                  subtitle="Select your desired integration to get started.">
                  {accesibleIntegrations.map((integration) => {
                    const objectType = 'enrichment';
                    const objectAction = 'update';
                    const objectSubtype = integration.type;

                    const hasPermission = userContainer.hasPermissionTo({
                      objectAction,
                      objectType,
                      objectSubtype,
                    });

                    return (
                      <IntegrationPickerCard
                        key={integration.title}
                        container={userContainer}
                        {...integration}
                        hasAccess
                        hasPermission={hasPermission}
                      />
                    );
                  })}
                </IntegrationPickerRow>
              )}
              {unaccesibleIntegrations.length > 0 && (
                <IntegrationPickerRow
                  title="Available integrations"
                  subtitle="Select your desired integration to get started."
                  secondary>
                  {unaccesibleIntegrations.map((integration) => {
                    return (
                      <IntegrationPickerCard
                        key={integration.title}
                        {...integration}
                        hasAccess={false}
                        setShowUpgradeForm={setShowUpgradeForm}
                      />
                    );
                  })}
                </IntegrationPickerRow>
              )}
            </div>

            <SidebarDrawer
              open={showUpgradeForm}
              onClose={() => {
                setShowUpgradeForm(false);
              }}>
              <UpgradeAccountForm
                onClose={() => {
                  setShowUpgradeForm(false);
                }}
                product="dataEnrichment"
              />
            </SidebarDrawer>
          </IntegrationPickerLayout>
        );
      }}
    </Subscribe>
  );
};

export default LegacyEnrichmentList;
